// TOEFL iBT Mock 4 — Reading (2026 format, two timed modules)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, memos).
//   academic – Read an Academic Passage: short passage, 5 questions
//              (the last question is a "where does this sentence fit?" item;
//              the [A]–[D] markers are part of the passage text).
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2.
//
// NOTE: Mock Test 4 in the PDF has a single Module 2, so `upper` and `lower`
// currently point to the same content. Replace `lower` with easier material
// if you want true adaptive routing.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

// ── Module 1 ─────────────────────────────────────────────────
const module1 = [
    {
      id: "m1-words",
      type: "words",
      title: "The Printing Press",
      text: "The invention of the mechanical printing press in the fifteenth century revolutionized the dissemination of knowledge across Europe. Before this grand [[innovation]], books [[were]] copied by [[hand]]. [[This]] meticulous [[process]] meant [[that]] written [[documents]] remained [[expensive]] [[and]] rare. [[When]] mass [[production]] became [[possible]], overall [[literacy]] began to [[rise]]. As information spread more rapidly, it accelerated scientific discovery and fueled significant cultural shifts that laid the groundwork for the modern information age.",
    },
    {
      id: "m1-daily1",
      type: "daily",
      title: "Package Pickup Change",
      text: `Package Pickup Change: Beginning April 8, packages for Maple Hall residents will be delivered to the service desk in Oak Hall while the Maple Hall lobby is painted. Residents will receive an email when a package is ready. Bring your student ID and collect packages within three days of the email notice. Refrigerated food deliveries will not be accepted during this period. The Maple Hall package room is expected to reopen on April 15.`,
      questions: [
        {
          question: "What is the main purpose of the notice?",
          options: [
            "To announce that Maple Hall is closing for repairs",
            "To ask residents to repaint the lobby",
            "To introduce a new food delivery service",
            "To explain a temporary change in package pickup location",
          ],
          correct: 3,
        },
        {
          question: "What must residents bring when collecting a package?",
          options: [
            "A room key and receipt",
            "A printed email notice",
            "Their student ID",
            "A refrigerated bag",
          ],
          correct: 2,
        },
        {
          question: "Which packages will not be accepted during this period?",
          options: [
            "Books from the campus store",
            "Refrigerated food deliveries",
            "Packages sent to Oak Hall",
            "Items collected within three days",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "m1-daily2",
      type: "daily",
      title: "Study Group Chat",
      text: `Maya: Hey, are you still coming to study group tonight?

Daniel: Yes! But I just saw that the library closes the third floor at 6.

Maya: Oh no. We booked room 304.

Daniel: I'll email the front desk. Maybe they can move us.

Maya: Or we could use the cafe next to the bookstore. It stays open until 9 and has outlets.

Daniel: Too noisy last time. What about the empty classroom in Hale Hall? Dr. Ortiz said students can use room 112 after 5.

Maya: Perfect. I'll text Sofia and Ben. Can you bring the markers for the whiteboard?

Daniel: Sure. Meet there at 6:30?

Maya: Make it 6:15. Ben has to leave by 8.`,
      questions: [
        {
          question: "What are Maya and Daniel mainly discussing?",
          options: [
            "How to reserve a room online",
            "Which class to take next semester",
            "Where to hold their study session",
            "Who should lead the study group",
          ],
          correct: 2,
        },
        {
          question: "Why does Daniel reject the cafe?",
          options: [
            "It closes too early.",
            "It was too noisy before.",
            "It has no outlets.",
            "It is far from campus.",
          ],
          correct: 1,
        },
        {
          question: "What does Maya ask Daniel to bring?",
          options: [
            "Markers",
            "Laptops",
            "Snacks",
            "Textbooks",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-academic",
      type: "academic",
      title: "Hydrothermal Vents and Deep-Sea Life",
      text: `Traditionally, scientists believed that all life on Earth ultimately depended on sunlight. The discovery of hydrothermal vents in 1977 fundamentally changed this understanding. Located along mid-ocean ridges where tectonic plates diverge, these fissures in the seafloor spew mineral-rich, superheated water into the freezing ocean. Despite complete darkness and extreme pressure, these harsh environments host thriving and complex biological communities.

Unlike surface ecosystems that rely on photosynthesis, the deep ocean lacks the sunlight necessary to support plant life. [A] Instead of using light, vent communities depend on an alternative process known as chemosynthesis. [B] In this process, specialized bacteria convert toxic chemicals dissolved in the vent fluids, such as hydrogen sulfide, into usable organic energy. [C] These bacteria form the base of the deep-sea food web, either growing in thick mats on the surrounding rocks or living symbiotically inside the bodies of larger animals. [D]

The most iconic residents of these ecosystems are giant tube worms, which can grow up to three meters in length. Lacking a mouth and a digestive tract entirely, these bizarre creatures depend exclusively on the chemosynthetic bacteria residing within a specialized internal organ called the trophosome. The worms absorb hydrogen sulfide and oxygen from the surrounding water and transport them to the bacteria, which in turn produce the carbohydrates that nourish their host.

Hydrothermal vent ecosystems are highly dynamic and transient. Because they are driven by geological and volcanic activity, individual vents may become inactive after only a few decades, abruptly cutting off the vital supply of chemical nutrients. When a vent dies, the local community perishes. However, marine biologists have discovered that vent organisms are remarkable colonizers. Their larvae drift along deep-ocean currents until they detect the chemical signatures of a newly formed vent, allowing these unique biological communities to regenerate across the vast ocean floor.`,
      questions: [
        {
          question: "The word \"transient\" in the passage is closest in meaning to",
          options: [
            "temporary",
            "predictable",
            "isolated",
            "complex",
          ],
          correct: 0,
        },
        {
          question: "According to paragraph 3, how do giant tube worms receive their nourishment?",
          options: [
            "They capture microscopic organisms floating in deep-ocean currents.",
            "They rely on bacteria living inside them to convert chemicals into food.",
            "They consume thick mats of bacteria found on the seafloor.",
            "They absorb carbohydrates directly from the mineral-rich vent water.",
          ],
          correct: 1,
        },
        {
          question: "According to the passage, all of the following are true about hydrothermal vent ecosystems EXCEPT:",
          options: [
            "They are located along mid-ocean ridges where tectonic plates separate.",
            "They rely on sunlight to generate energy for the food web.",
            "They are characterized by high pressure and complete darkness.",
            "They can become inactive when local volcanic activity ceases.",
          ],
          correct: 1,
        },
        {
          question: "Look at the four squares [A], [B], [C], and [D] that indicate where the following sentence could be added to the passage. \"Consequently, organisms in these environments have had to evolve entirely different methods of survival.\" Where would the sentence best fit?",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 0,
        },
        {
          question: "What can be inferred from paragraph 4 about the larvae of vent organisms?",
          options: [
            "They are unable to survive in the open ocean without a constant supply of hydrogen sulfide.",
            "They possess a mechanism to sense specific chemical compounds in the water.",
            "They mature into adult organisms while drifting in deep-ocean currents.",
            "They travel exclusively to older, established vents rather than newly formed ones.",
          ],
          correct: 1,
        },
      ],
    },
];

// ── Module 2 ─────────────────────────────────────────────────
const module2 = [
    {
      id: "m2-words",
      type: "words",
      title: "Seed Dispersal",
      text: "Seed dispersal is the process by which plants move their seeds away from the parent plant to new locations. [[This]] movement [[reduces]] competition [[for]] light, [[water]], and [[nutrients]] between [[young]] seedlings [[and]] their [[parents]]. Some [[plants]] produce [[light]] seeds [[with]] thin [[wings]] or fluffy [[hairs]] that [[travel]] long [[distances]] on the [[wind]]. Others grow sweet fruits that attract animals, which eat the fruit and later drop the seeds in different places. A few species even use water, floating their seeds along rivers until they settle on distant banks. Because each method suits a particular environment, the way a plant spreads its seeds often reflects the habitat where it evolved.",
    },
    {
      id: "m2-daily1",
      type: "daily",
      title: "Fall Swim Lessons",
      text: `Riverside Community Center - Fall Swim Lessons, Saturdays

Starting September 13 (8 weeks)

9:00 a.m. - Parent and Baby (ages 6-24 months), Shallow Pool

9:45 a.m. - Beginners (ages 4-6), Shallow Pool

10:30 a.m. - Intermediate (ages 7-10), Main Pool, Lanes 1-2

11:15 a.m. - Advanced Strokes (ages 11-14), Main Pool, Lanes 3-4

12:00 p.m. - Open swim (all ages; no lessons)

Fee: $96 per child for members, $128 for non-members. Register online or at the front desk by September 5. Classes are limited to 8 swimmers. There will be no lessons on October 11 because of a swim meet. Swimmers must shower before entering the pool.`,
      questions: [
        {
          question: "At what time should an eight-year-old child attend lessons?",
          options: [
            "9:45 a.m.",
            "11:15 a.m.",
            "9:00 a.m.",
            "10:30 a.m.",
          ],
          correct: 3,
        },
        {
          question: "Which of the following is NOT stated in the schedule?",
          options: [
            "Lessons are canceled on October 11.",
            "Registration closes on September 5.",
            "The instructors' qualifications",
            "Each class has a limit of eight swimmers.",
          ],
          correct: 2,
        },
        {
          question: "How much more does a non-member pay than a member for one child?",
          options: [
            "$16",
            "$32",
            "$96",
            "$128",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "m2-daily2",
      type: "daily",
      title: "Juniper Corner Cafe Notice",
      text: `Notice from Juniper Corner Cafe

Beginning Monday, November 3, we are updating our menu for the winter season. Our summer iced teas and the cucumber sandwich will be retired. In their place, we will offer a spiced apple cider ($4.25), a roasted tomato soup served with grilled bread ($7.50), and a turkey and cranberry panini ($9.75).

Most prices will stay the same, but the price of a large coffee will rise from $3.50 to $3.75 because of higher bean costs. Our loyalty card program is unchanged: collect ten stamps and receive a free drink. We will also close at 6:00 p.m. instead of 8:00 p.m. on weekdays through the end of February. Thank you for your continued support.`,
      questions: [
        {
          question: "In the notice, the word \"retired\" is closest in meaning to",
          options: [
            "rested",
            "delayed",
            "discontinued",
            "discounted",
          ],
          correct: 2,
        },
        {
          question: "Which of the following will NOT change?",
          options: [
            "The loyalty card program",
            "The weekday closing time",
            "The price of a large coffee",
            "The cucumber sandwich",
          ],
          correct: 0,
        },
        {
          question: "Why is the price of a large coffee going up?",
          options: [
            "The cafe is closing earlier.",
            "The cafe is adding new menu items.",
            "Winter drinks cost more to make.",
            "Coffee beans have become more expensive.",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m2-academic",
      type: "academic",
      title: "The Rise of the Piano",
      text: `The piano, now a fixture of concert halls and homes worldwide, was invented around 1700 by Bartolomeo Cristofori, an instrument maker employed at the court of the Medici family in Florence. Cristofori sought to improve on the harpsichord, whose strings are plucked mechanically and therefore sound at nearly the same volume no matter how forcefully a key is struck. His new instrument used small hammers to strike the strings, allowing performers to vary loudness through the force of their touch.

Early pianos were lightly built, with wooden frames and thin strings, and their tone was delicate compared with that of modern instruments. [A] Throughout the eighteenth century, makers in Germany, Austria, and England refined the mechanism, and composers such as Mozart gradually embraced the new instrument. [B] Beethoven, in particular, repeatedly pressed makers for greater power and a wider range of notes, and his compositions tested the limits of the pianos available to him.

The Industrial Revolution transformed piano design. Iron frames, which could withstand the enormous combined tension of the strings, replaced wooden ones, and makers began using steel wire of higher quality. [C] The result was a louder, richer sound that could fill large public halls. Mass production lowered costs, and by the nineteenth century the piano had become a symbol of middle-class respectability, with many households purchasing one for family music-making.

Today the piano remains remarkably central to Western music, serving as a solo instrument, an accompaniment, and a tool for composition and teaching. [D] Digital keyboards that imitate its sound have become popular, yet the acoustic piano's essential mechanism, descended directly from Cristofori's hammer action, has changed little in principle. Its repertoire spans centuries, from Baroque keyboard works to contemporary jazz.`,
      questions: [
        {
          question: "The word \"embraced\" in the passage is closest in meaning to",
          options: [
            "criticized",
            "inherited",
            "accepted",
            "imitated",
          ],
          correct: 2,
        },
        {
          question: "According to paragraph 1, how did Cristofori's instrument differ from the harpsichord?",
          options: [
            "Its strings were plucked by small metal hooks.",
            "Players could vary loudness by changing how hard they struck the keys.",
            "It could be played only at a soft volume.",
            "It was designed for use in churches rather than courts.",
          ],
          correct: 1,
        },
        {
          question: "What can be inferred from paragraph 3 about early wooden piano frames?",
          options: [
            "They produced a louder sound than iron frames.",
            "They were more expensive than iron frames.",
            "They were preferred by middle-class households.",
            "They could not safely support very high string tension.",
          ],
          correct: 3,
        },
        {
          question: "Why does the author mention Beethoven in paragraph 2?",
          options: [
            "To show that composers encouraged improvements in piano design",
            "To prove that Mozart disliked the piano",
            "To explain why pianos became cheaper",
            "To argue that early pianos were superior to modern ones",
          ],
          correct: 0,
        },
        {
          question: "Where would the following sentence best fit? \"These changes allowed the strings to be stretched far more tightly without damaging the instrument.\"",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 2,
        },
      ],
    },
];

export const readingModules = {
  module1,
  upper: module2,
  lower: module2,
};

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}